import React, { useCallback, useState } from "react";
import { api } from "@/lib/api";
import { UploadSimpleIcon, FileXlsIcon, CheckCircleIcon, WarningCircleIcon, DownloadSimpleIcon, WrenchIcon } from "@phosphor-icons/react";

export default function Upload() {
  const [dragging, setDragging] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const onFile = useCallback(async (file) => {
    if (!file) return;
    setError(null);
    setResult(null);
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post("/upload-excel", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  }, []);

  return (
    <div data-testid="upload-page" className="p-6 space-y-4 max-w-5xl">
      <div>
        <div className="text-[10px] mono uppercase tracking-widest text-[#64748B]">Data Ingestion · Phase 0</div>
        <h1 className="text-2xl tracking-tight font-medium mt-1">Upload Your Excel</h1>
        <p className="text-sm text-[#94A3B8] mt-2 max-w-2xl leading-relaxed">
          Drop the Excel workbook your AI system writes (any combination of dashboard / leaderboard / monthly / bot config sheets). BotGenomix will parse every sheet and show a preview so we can confirm the mapping before fully ingesting into the dashboard.
        </p>
      </div>

      {/* Stage B + C patches download */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <a
          href="/BotGenomix-StageB-Patches.zip"
          download
          data-testid="download-stage-b"
          className="block border border-[#2A2D3A] bg-[#15171E] rounded-md p-4 hover:border-[#94A3B8]/40 transition-colors opacity-80"
        >
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-md bg-white/[0.04] border border-[#2A2D3A] flex items-center justify-center shrink-0">
              <WrenchIcon size={18} color="#94A3B8" weight="duotone" />
            </div>
            <div className="flex-1 min-w-0">
              <span className="text-[10px] mono uppercase tracking-widest text-[#94A3B8]">Stage B · Surgical Fix Pack</span>
              <div className="text-sm text-[#F1F5F9] mt-1">PowerShell stability patches</div>
              <p className="text-xs text-[#94A3B8] mt-1 leading-relaxed">
                Fixes the 4 original bugs (300 clones, dup names, Excel skips, pipeline death). Install Stage B first if you haven&apos;t already.
              </p>
              <div className="flex items-center gap-2 mt-2 text-[11px] mono text-[#94A3B8]">
                <DownloadSimpleIcon size={12} /> BotGenomix-StageB-Patches.zip
              </div>
            </div>
          </div>
        </a>

        <a
          href="/BotGenomix-StageC-Patches.zip"
          download
          data-testid="download-stage-c"
          className="block border border-[#D6FF38]/30 bg-[#D6FF38]/[0.04] rounded-md p-4 hover:bg-[#D6FF38]/[0.07] transition-colors"
        >
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-md bg-[#D6FF38]/10 border border-[#D6FF38]/30 flex items-center justify-center shrink-0">
              <WrenchIcon size={18} color="#D6FF38" weight="duotone" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-[10px] mono uppercase tracking-widest text-[#D6FF38]">Stage C · Real AI Brain</span>
                <span className="text-[9px] mono uppercase tracking-widest px-1.5 py-0.5 rounded-sm bg-[#D6FF38]/15 text-[#D6FF38] border border-[#D6FF38]/30">NEW</span>
              </div>
              <div className="text-sm text-[#F1F5F9] mt-1">Claude Sonnet 4.6 + hypothesis tracking</div>
              <p className="text-xs text-[#94A3B8] mt-1 leading-relaxed">
                Replaces rule-based judge with a real LLM brain. Hypothesis-driven mutations, learning loop, ~$3-6/month. Requires Python 3.10+ and `EMERGENT_LLM_KEY`. Install Stage B first.
              </p>
              <div className="flex items-center gap-2 mt-2 text-[11px] mono text-[#D6FF38]">
                <DownloadSimpleIcon size={12} /> BotGenomix-StageC-Patches.zip
              </div>
            </div>
          </div>
        </a>
      </div>

      <div
        data-testid="upload-zone"
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragging(false);
          const f = e.dataTransfer.files?.[0];
          onFile(f);
        }}
        className={`border-2 border-dashed rounded-md p-12 text-center transition-all ${
          dragging ? "border-[#D6FF38] bg-[#D6FF38]/5" : "border-[#2A2D3A] bg-[#15171E]"
        }`}
      >
        <div className="mx-auto w-14 h-14 rounded-md bg-[#D6FF38]/10 flex items-center justify-center mb-3">
          <UploadSimpleIcon size={26} color="#D6FF38" weight="duotone" />
        </div>
        <div className="text-lg text-[#F1F5F9]">Drop .xlsx or .xls here</div>
        <div className="text-xs mono text-[#64748B] mt-1 uppercase tracking-widest">or</div>
        <label className="inline-block mt-3 cursor-pointer">
          <input
            type="file"
            accept=".xlsx,.xls"
            data-testid="file-input"
            className="hidden"
            onChange={(e) => onFile(e.target.files?.[0])}
          />
          <span className="inline-flex items-center gap-2 bg-[#D6FF38] text-black mono uppercase tracking-widest text-xs font-semibold px-4 py-2 rounded-md hover:bg-[#BDF000] transition-colors">
            Browse file
          </span>
        </label>
        {busy && <div className="mt-4 text-xs mono text-[#94A3B8]">parsing…</div>}
      </div>

      {error && (
        <div className="border border-[#FF4D4D]/30 bg-[#FF4D4D]/5 rounded-md p-3 flex items-center gap-2 text-sm text-[#FF4D4D]" data-testid="upload-error">
          <WarningCircleIcon size={16} /> {error}
        </div>
      )}

      {result && (
        <div className="space-y-3" data-testid="upload-result">
          <div className="flex items-center gap-2 text-sm text-[#2ECA7F]">
            <CheckCircleIcon size={16} weight="fill" /> Parsed <span className="mono">{result.filename}</span> — {result.sheets.length} sheets
          </div>
          {result.sheets.map((s) => (
            <details key={s.name} className="border border-[#2A2D3A] bg-[#15171E] rounded-md">
              <summary className="cursor-pointer px-4 py-3 flex items-center gap-2 hover:bg-white/[0.02]">
                <FileXlsIcon size={14} className="text-[#D6FF38]" />
                <span className="mono text-sm text-[#F1F5F9]">{s.name}</span>
                {s.rows !== undefined && <span className="text-[11px] mono text-[#64748B]">{s.rows} rows · {s.columns?.length || 0} cols</span>}
                {s.error && <span className="text-[11px] mono text-[#FF4D4D]">error: {s.error}</span>}
              </summary>
              {s.columns && (
                <div className="px-4 pb-4">
                  <div className="text-[10px] mono uppercase tracking-widest text-[#64748B] mb-2">Columns</div>
                  <div className="flex flex-wrap gap-1.5 mb-3">
                    {s.columns.map((c) => (
                      <span key={c} className="text-[11px] mono px-2 py-0.5 rounded-sm border border-[#2A2D3A] text-[#94A3B8]">{c}</span>
                    ))}
                  </div>
                  {s.preview && s.preview.length > 0 && (
                    <div className="overflow-x-auto">
                      <table className="text-xs w-full">
                        <thead>
                          <tr className="border-b border-[#2A2D3A]">
                            {s.columns.map((c) => (
                              <th key={c} className="text-left py-1.5 pr-3 mono text-[10px] uppercase tracking-widest text-[#64748B] font-normal">{c}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {s.preview.map((row, i) => (
                            <tr key={`${s.name}-row-${i}-${Object.values(row)[0] ?? ""}`} className="border-b border-[#2A2D3A]/30">
                              {s.columns.map((c) => (
                                <td key={c} className="py-1.5 pr-3 mono text-[#F1F5F9]">{String(row[c] ?? "").slice(0, 40)}</td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
            </details>
          ))}
          <div className="text-[11px] mono text-[#64748B] pt-2">
            Send me a screenshot of any sheet that doesn&apos;t parse cleanly and I&apos;ll map it in. In Phase 1 we&apos;ll wire your local agent to push these automatically.
          </div>
        </div>
      )}
    </div>
  );
}
